// #1155 S-MN1 step 1 — which Number an "add gateway" click is for. Pure: the page feeds it the server's
// verdicts (`GET /numbers`) and renders the result.
//
// The server decides what is allowed (`canAddEngine` / `canAddNewNumber` are the create gate's own rule
// run per target); this module only turns those verdicts into a UI step. It never recomputes a limit.
//
// THE N=1 RULE (do not "simplify" it away): an account with a single Number sends NO target at all. Its
// create body is exactly today's. `{ newNumber: true }` is DENIED for such an account whenever its Number
// row exists (the row outlives a deleted gateway), so sending a target for N=1 would break re-adding a
// gateway. Only an account the operator raised above one Number ever gets a target, or a picker.

export interface NumberOption {
  _id: string;
  phoneNumber: string;
  label: string | null;
  status: 'active' | 'disconnected';
  hasEngine: boolean;
  /** The create gate would accept `{ numberId: _id }` now. */
  canAddEngine: boolean;
}

/** The `GET /numbers` payload. */
export interface NumbersView {
  maxNumbers: number;
  /** The create gate would accept `{ newNumber: true }` now. */
  canAddNewNumber: boolean;
  numbers: NumberOption[];
}

export type AddTarget = { numberId: string } | { newNumber: true };

export type AddChoice =
  | { kind: 'number'; number: NumberOption }
  | { kind: 'new' };

export type AddPlan =
  /** Nothing to pick: the page keeps today's behaviour (create with no target; the server gate decides). */
  | { step: 'default' }
  /** No target is addable: show the quota dialog. */
  | { step: 'blocked' }
  /** Exactly one addable target: use it, no picker. */
  | { step: 'create'; target: AddTarget }
  /** Two or more: let the user choose. */
  | { step: 'choose'; choices: AddChoice[] };

export function targetOf(choice: AddChoice): AddTarget {
  return choice.kind === 'new' ? { newNumber: true } : { numberId: choice.number._id };
}

export function planAddGateway(view: NumbersView | undefined): AddPlan {
  // Still loading, or the read failed: today's behaviour. The server gate stays authoritative.
  if (!view) return { step: 'default' };
  // N=1 (and anything unrecognised): no target, ever. See the rule above.
  if (!(view.maxNumbers > 1)) return { step: 'default' };

  const choices: AddChoice[] = view.numbers.filter((n) => n.canAddEngine).map((number) => ({ kind: 'number', number }));
  if (view.canAddNewNumber) choices.push({ kind: 'new' });

  if (choices.length === 0) return { step: 'blocked' };
  if (choices.length === 1) return { step: 'create', target: targetOf(choices[0]) };
  return { step: 'choose', choices };
}

/** The body of the `sessions` create: today's fields plus the target, if any. */
export function remoteSessionBody(owner: string, target?: AddTarget) {
  return {
    owner,
    engineType: 'session-pod' as const,
    gatewayType: 'wa-js-gcp' as const,
    status: 'initializing' as const,
    active: true,
    k8sNamespace: 'blueticks-adhock-gateways' as const,
    ...target,
  };
}

/** What the picker shows for a Number: its label if it has one, else the phone. */
export function numberTitle(n: Pick<NumberOption, 'label' | 'phoneNumber'>): string {
  return n.label?.trim() || n.phoneNumber;
}
